import _get from "lodash/get";
import _size from "lodash/size";
import _uniq from "lodash/uniq";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { setValue } from "./personalDetailsActions";
import {
    CUSTOMER_TYPE,
    CustomerType,
    FORM_ID,
    FORMATTED_PHONE_NUMBER_LEN,
    NUMBER_TYPE,
    API_URL_CHECK_CUSTOMER_AND_SET_PERSONAL_DETAILS_FOR_NEW_MEMBER,
    API_URL_CHECK_PHONE_NUMBER_FOR_NEW_MEMBER,
    API_URL_UPDATE_PORTIN_DATE,
} from "../../../../../constants";
import { RootState, ICartItemWithBundleId, HolidayData } from "../../../../../models";
import { CheckCustomerAndSetPersonalDetailsForNewMemberRequestBody } from "../../../../../models/actionPayloads";
import { StepId } from "../../../../../models/state";
import { get as Ajax_get, post as Ajax_post } from "../../../app/Ajax";
import { setNegativeFlash } from "../../../app/state/generalActions";
import { actions as RAPIDFORM_ACTIONS, actionTypes as RF_AT } from "../../../rapidform";
import {
    allValidationRules,
    getFormattedPhoneNumber,
    getFormattedMBBNumber,
    maskField,
    VALIDATION_FIELD,
} from "../../../rapidform/formValidation";
import { API_URL_AVAILABLE_NUMBERS, API_URL_GET_EXTRA_SIM_PRICES, API_URL_GET_HOLIDAY_DATA } from "../../api";
import TEXT from "../../text-consts";
import { fulfilled, isPhoneNumberUsable, isValidSubscriptionAge } from "../../utils";
import {
    CHECKOUT_SET_SELECTED_PHONE_NUMBER,
    CHECKOUT_CHOOSE_PHONE_NUMBER_SWITCH_PANEL,
    CHECKOUT_FETCH_AVAILABLE_PHONE_NUMBERS,
    CHECKOUT_CHECK_NEW_MEMBER_PHONE_NUMBER,
    CHECKOUT_SET_MEMBER_DETAILS,
    CHECKOUT_SET_SELECTED_MEMBER_STEP,
    CHECKOUT_GET_EXTRA_SIM_PRICES,
    CHECKOUT_UPDATE_PORTIN_DATE,
} from "../actionTypes";
import { getCurrentSubscriptionMemberId } from "../selectors/accountSelectors";
import { hasReachedMaximumNumberOfNewSubscriptions } from "../selectors/combinedSelectors";
import * as CUSTOMER_INFO_SELECTORS from "../selectors/customerInfoSelectors";
import { getFormById } from "../selectors/formSelectors";
import { getPhoneNumberCheckType, getSelectedPhoneNumber } from "../selectors/phoneNumberSelectors";
import { getAvailablePhoneNumbers, getSelectedPanel } from "../selectors/phoneNumbersSelectors";
import {
    getProductUser,
    getSubscriptionMaxAgeAllowed,
    getSubscriptions,
    isServiceSubscription as isServiceSubscriptionSelector,
    isProductUserMandatory as isProductUserMandatorySelector,
    getSubscription,
} from "../selectors/subscriptionsSelectors";
import { isMhsSubscription } from "../../../../../shared/subscriptionCodeUtils";

export const setSelectedPhoneNumber =
    (
        selectedPhoneNumber: string,
        customerType?: CustomerType,
        id?: string,
        editMode = false,
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const currentSubscriptionMemberId = id ?? getCurrentSubscriptionMemberId(state);
        const formatter = isServiceSubscriptionSelector(state, currentSubscriptionMemberId)
            ? getFormattedMBBNumber
            : getFormattedPhoneNumber;

        dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.ADD_MEMBER),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.ADD_MEMBER,
                inputName: `member-number`,
                inputValue: formatter(selectedPhoneNumber),
                visible: false,
            },
        });

        dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.ADD_MEMBER),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.ADD_MEMBER,
                inputName: `member-type`,
                inputValue: customerType,
                visible: false,
            },
        });

        dispatch({
            type: CHECKOUT_SET_SELECTED_PHONE_NUMBER,
            subscriptionId: currentSubscriptionMemberId,
            allowedToProceed: editMode,
            customerType,
            selectedPhoneNumber,
        });
    };

export const prefillMemberform =
    (id?: string, removed = false, editMode = false): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();

        const productUser = getProductUser(state, id);
        const productUserPhoneNumber = getSelectedPhoneNumber(state, id);
        const productUserType = getPhoneNumberCheckType(state, id);
        const availablePhoneNumbers = getAvailablePhoneNumbers(state);

        const activePanel = productUserType || CUSTOMER_TYPE.NEW;
        dispatch({ type: CHECKOUT_CHOOSE_PHONE_NUMBER_SWITCH_PANEL, activePanel });
        if (!removed) {
            dispatch(setSelectedPhoneNumber(productUserPhoneNumber, productUserType, id, editMode));

            if (activePanel === CUSTOMER_TYPE.NEW) {
                dispatch({
                    type: fulfilled(CHECKOUT_FETCH_AVAILABLE_PHONE_NUMBERS),
                    payload: _uniq([productUserPhoneNumber, ...availablePhoneNumbers]),
                });
            }
        }

        const mapper = (field: string) => {
            const internalValueMap: Record<string, string> = {
                [VALIDATION_FIELD.PRODUCT_USER_EMAIL]: "email",
                [VALIDATION_FIELD.PRODUCT_USER_CITY]: "city",
                [VALIDATION_FIELD.PRODUCT_USER_POSTCODE]: "postcode",
                [VALIDATION_FIELD.PRODUCT_USER_STREET]: "street",
            };
            return internalValueMap[field] || field;
        };

        [
            VALIDATION_FIELD.BIRTHDATE,
            VALIDATION_FIELD.FIRST_NAME,
            VALIDATION_FIELD.LAST_NAME,
            VALIDATION_FIELD.PRODUCT_USER_STREET,
            VALIDATION_FIELD.PRODUCT_USER_POSTCODE,
            VALIDATION_FIELD.PRODUCT_USER_CITY,
            VALIDATION_FIELD.PRODUCT_USER_EMAIL,
        ].forEach((field) => {
            dispatch({
                type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.ADD_MEMBER),
                payload: {
                    errorMessage: undefined,
                    form: FORM_ID.ADD_MEMBER,
                    inputName: `member-${field}`,
                    // oxlint-disable-next-line typescript/ban-ts-comment
                    // @ts-expect-error
                    inputValue: removed ? "" : productUser[mapper(field)],
                    visible: true,
                },
            });
        });
    };

export const fetchAvailableNumbers =
    (
        setFirstAsSubscriptionNumber = false,
        subscriptionId = "",
    ): ThunkAction<Promise<string[]>, RootState, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const state = getState();

        const availablePhoneNumbers = getAvailablePhoneNumbers(state);
        if (availablePhoneNumbers.length > 0) {
            if (setFirstAsSubscriptionNumber) {
                dispatch(setSelectedPhoneNumber(availablePhoneNumbers[0], CUSTOMER_TYPE.NEW, subscriptionId));
            }
            return Promise.resolve(availablePhoneNumbers);
        }

        const numberType = isServiceSubscriptionSelector(state, subscriptionId)
            ? NUMBER_TYPE.TWELVE_DIGITS_VAS
            : NUMBER_TYPE.EIGHT_DIGITS;
        const response: Promise<string[]> = Ajax_get(
            { url: API_URL_AVAILABLE_NUMBERS(numberType, subscriptionId) },
            dispatch,
        );

        dispatch({
            type: CHECKOUT_FETCH_AVAILABLE_PHONE_NUMBERS,
            payload: response,
            meta: {
                flashOnError: {
                    defaultErrorMessage: TEXT.ERROR.FETCH_PHONE_NUMBER,
                },
            },
        });

        if (setFirstAsSubscriptionNumber) {
            return response.then((payload) => {
                dispatch(setSelectedPhoneNumber(payload[0], CUSTOMER_TYPE.NEW, subscriptionId));
                return payload;
            });
        }

        return response;
    };

export const checkMemberPhoneNumber =
    (
        phoneNumber?: string,
        memberId?: string,
        activePanel?: CustomerType,
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        const onlyDigits = (phoneNumber ?? "").replace(/\s/g, "");

        if (!isPhoneNumberUsable(onlyDigits)) {
            return dispatch(setNegativeFlash(TEXT.ERROR.INVALID_PHONE_NUMBER_FORMAT));
        }

        return dispatch({
            type: CHECKOUT_CHECK_NEW_MEMBER_PHONE_NUMBER,
            payload: Ajax_post(
                {
                    url: API_URL_CHECK_PHONE_NUMBER_FOR_NEW_MEMBER,
                    data: {
                        phoneNumber: onlyDigits,
                        memberId,
                        activePanel,
                    },
                },
                dispatch,
            ),
            meta: {
                flashOnError: {
                    defaultErrorMessage: TEXT.ERROR.CHECK_PHONE_NUMBER,
                },
            },
        });
    };

export const chooseMemberPhoneNumber =
    (newPhoneNumber: string, memberId?: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const currentMemberId = memberId ?? getCurrentSubscriptionMemberId(state);
        const activePanel = getSelectedPanel(state);
        const customerType = activePanel || CUSTOMER_TYPE.EXISTING;
        // TODO: Can 'member-number' in MEMBER_FORM be used instead?
        const selectedPhoneNumber = getSelectedPhoneNumber(state, currentMemberId);

        // If called with empty string but there's already a valid phone number, don't override it
        if (!newPhoneNumber && selectedPhoneNumber && isPhoneNumberUsable(selectedPhoneNumber)) {
            return;
        }

        const validationRule = allValidationRules[VALIDATION_FIELD.SUBSCRIPTION_NUMBER];

        const maskedNumber = maskField((newPhoneNumber || "").replace(/\s/g, ""), selectedPhoneNumber, validationRule);

        if (maskedNumber !== selectedPhoneNumber) {
            dispatch(setSelectedPhoneNumber(maskedNumber, customerType, currentMemberId));
        }

        // Here it is either port-in or existing customer.
        // Maybe check phone number
        if (([CUSTOMER_TYPE.EXISTING, CUSTOMER_TYPE.PORT_IN] as CustomerType[]).includes(activePanel)) {
            if (maskedNumber.length >= FORMATTED_PHONE_NUMBER_LEN && maskedNumber !== selectedPhoneNumber) {
                return dispatch(checkMemberPhoneNumber(maskedNumber, currentMemberId, activePanel));
            }
        }
    };

export const chooseMemberPhoneNumberSwitchPanel =
    (activePanel: CustomerType, memberId?: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const currentSub = getSubscription(state, memberId);
        if (
            activePanel === CUSTOMER_TYPE.NEW &&
            hasReachedMaximumNumberOfNewSubscriptions(state) &&
            isMhsSubscription(currentSub?.code)
        ) {
            return dispatch(setNegativeFlash(TEXT.ERROR.MAX_NUMBER_OF_NEW_NUMBERS_REACHED));
        }

        // Reset campaign flag
        dispatch(setValue("isCampaignAllowed", true));
        dispatch(setValue("campaignIdsNotAllowed", []));

        // Reset age check
        dispatch(setValue("isProductUserAgeOk", true));

        // Reset number immediately for PORT_IN and EXISTING to prevent new number being shown in input field
        if (([CUSTOMER_TYPE.EXISTING, CUSTOMER_TYPE.PORT_IN] as CustomerType[]).includes(activePanel)) {
            dispatch(setSelectedPhoneNumber(""));
        }

        // Reset available numbers so they are not temporary shown while fetching new ones.
        if (activePanel === CUSTOMER_TYPE.NEW) {
            dispatch({
                type: fulfilled(CHECKOUT_FETCH_AVAILABLE_PHONE_NUMBERS),
                payload: [],
            });
        }

        // Set panel side, clear form and reset memberId in order
        // If switching to new number panel => fetch new numbers and set first to selected
        dispatch({ type: CHECKOUT_CHOOSE_PHONE_NUMBER_SWITCH_PANEL, activePanel });
        dispatch(RAPIDFORM_ACTIONS.clearAll(FORM_ID.ADD_MEMBER));

        // Both does a saveOrder in backend, so we need to do them one after the other.
        // Do reset first, as fetch number promise populate subscriptions part of store
        if (activePanel === CUSTOMER_TYPE.NEW) {
            dispatch(fetchAvailableNumbers(true, memberId));
        }
        if (activePanel === CUSTOMER_TYPE.EXISTING) {
            const numberOfSubscriptions = _size(getSubscriptions(state));
            if (numberOfSubscriptions === 1) {
                dispatch(chooseMemberPhoneNumber("", memberId));
            } else {
                dispatch(chooseMemberPhoneNumber("", memberId));
            }
        }
    };

interface MemberDetailsResponse {
    hasUpdatedMember?: boolean;
    isCreditCheckUnavailable?: boolean;
    isNewNumberWithPotentialFraud?: boolean;
    minUpfrontPrice?: number;
}

export const setMemberDetails =
    ({
        memberId,
        campaignId,
        isOptingOutForTeliaXServices = false,
    }: {
        campaignId?: string;
        isOptingOutForTeliaXServices?: boolean;
        memberId: string;
    }): ThunkAction<Promise<MemberDetailsResponse>, RootState, unknown, AnyAction> =>
    async (dispatch, getState): Promise<MemberDetailsResponse> => {
        const state = getState();
        const { values } = getFormById(state, FORM_ID.ADD_MEMBER);
        const isCatalogListingOk = CUSTOMER_INFO_SELECTORS.isCatalogListingOk(state);
        const isSecretNumber = CUSTOMER_INFO_SELECTORS.isSecretNumber(state);
        const isEsimSelected = CUSTOMER_INFO_SELECTORS.isEsimSelected(state);
        const esimType = CUSTOMER_INFO_SELECTORS.esimType(state);
        const isBarringsUsed = CUSTOMER_INFO_SELECTORS.isBarringsUsed(state);
        const isCampaignToggled = CUSTOMER_INFO_SELECTORS.isCampaignToggled(state);
        const isProductUserMandatory = isProductUserMandatorySelector(state, memberId);
        const isProductUserDifferentThanLegalOwner =
            CUSTOMER_INFO_SELECTORS.isProductUserDifferentThanLegalOwner(state) || isProductUserMandatory;
        const birthDate = isProductUserDifferentThanLegalOwner
            ? _get(values, `member.${VALIDATION_FIELD.BIRTHDATE}`, null)
            : CUSTOMER_INFO_SELECTORS.getLegalOwnerBirthdate(state);
        const isEmailUsedForTeliaXServices =
            CUSTOMER_INFO_SELECTORS.isEmailTakenForTeliaTrygg(state) ||
            CUSTOMER_INFO_SELECTORS.isEmailTakenForTeliaSky(state);
        const isServiceSubscription = isServiceSubscriptionSelector(state, memberId);

        const subscriptionMaxAgeAllowed = getSubscriptionMaxAgeAllowed(state, memberId);

        // Check if age is ok - for age-restricted subscriptions, always validate even when legal owner = product user
        // This is important for existing customers with JUNIOR/BARN subscriptions who may have aged out
        const isProductUserAgeOk = isValidSubscriptionAge({
            subscriptionMaxAgeAllowed,
            productUserBirthDate: birthDate,
            useProductUserAge: true,
        });

        if (!isProductUserAgeOk && subscriptionMaxAgeAllowed) {
            dispatch(setValue("isProductUserAgeOk", false));
            return { hasUpdatedMember: false };
        } else {
            dispatch(setValue("isProductUserAgeOk", true));
        }

        const productUser = {
            firstName: values.member[VALIDATION_FIELD.FIRST_NAME],
            lastName: values.member[VALIDATION_FIELD.LAST_NAME],
            postcode: values.member[VALIDATION_FIELD.PRODUCT_USER_POSTCODE],
            email: values.member[VALIDATION_FIELD.PRODUCT_USER_EMAIL],
            birthDate: values.member[VALIDATION_FIELD.BIRTHDATE],
        };

        let teliaXServicesEmail;
        if (isEmailUsedForTeliaXServices || isServiceSubscription) {
            teliaXServicesEmail = productUser.email;
        }

        const memberContainer: CheckCustomerAndSetPersonalDetailsForNewMemberRequestBody = {
            // @ts-expect-error: fields in productUser is any
            productUser,
            phoneNumber: {
                number: (values.member.number || "").replace(/\s/g, ""),
                type: values.member.type || null,
            },
            isCatalogListingOk,
            isEsimSelected,
            esimType,
            isBarringToggleOn: isBarringsUsed,
            isProductUserDifferentThanLegalOwner,
            campaignId: isCampaignToggled ? campaignId : undefined,
            newMemberId: memberId,
            existingMemberId: memberId,
            isSecretNumber,
            isOptingOutForTeliaXServices,
            teliaXServicesEmail,
        };

        const setMemberPromise = Ajax_post<{
            cart: { content?: ICartItemWithBundleId[] };
            hasUpdatedMember?: boolean;
            isEmailTakenForTeliaSky?: boolean;
            isCreditCheckUnavailable?: boolean;
            isEmailTakenForTeliaTrygg?: boolean;
            isNewNumberWithPotentialFraud?: boolean;
            isNotFound?: boolean;
            isZipNeeded?: boolean;
            minUpfrontPrice?: number;
            shouldAddTeliaSky?: boolean;
            shouldAddTeliaTrygg?: boolean;
        }>(
            {
                url: API_URL_CHECK_CUSTOMER_AND_SET_PERSONAL_DETAILS_FOR_NEW_MEMBER,
                data: memberContainer,
            },
            dispatch,
        ).then((data) => {
            if (data.isEmailTakenForTeliaSky || data.isEmailTakenForTeliaTrygg) {
                return data;
            }

            const minUpfrontPrice = data.minUpfrontPrice;

            if (data.isNewNumberWithPotentialFraud || (typeof minUpfrontPrice === "number" && minUpfrontPrice > 0)) {
                return {
                    isCreditCheckUnavailable: !!data.isCreditCheckUnavailable,
                    isNewNumberWithPotentialFraud: !!data.isNewNumberWithPotentialFraud,
                    minUpfrontPrice,
                };
            }

            if (data.isZipNeeded || data.isNotFound) {
                dispatch(setValue("isZipNeeded", !!data.isZipNeeded));
                dispatch(setValue("isNotFound", !!data.isNotFound));
                return {
                    isZipNeeded: !!data.isZipNeeded,
                    isNotFound: !!data.isNotFound,
                };
            }

            return { hasUpdatedMember: true, ...data };
        });

        dispatch({
            type: CHECKOUT_SET_MEMBER_DETAILS,
            payload: setMemberPromise,
        });

        return setMemberPromise;
    };

export const getExtraSimPrices =
    (memberId = "", phoneNumber = ""): ThunkAction<Promise<string[]>, RootState, unknown, AnyAction> =>
    async (dispatch) => {
        const response: Promise<string[]> = Ajax_get(
            {
                url: API_URL_GET_EXTRA_SIM_PRICES(memberId, phoneNumber),
            },
            dispatch,
        );

        dispatch({
            type: CHECKOUT_GET_EXTRA_SIM_PRICES,
            payload: response,
        });

        return response;
    };

export const setSelectedStep = (selectedStep?: StepId) => ({
    type: CHECKOUT_SET_SELECTED_MEMBER_STEP,
    payload: {
        selectedStep,
    },
});

export const updatePortinDate =
    (
        portinDate: string,
        memberId: string,
        portingDateSelectionType?: "asap" | "custom",
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        return dispatch({
            type: CHECKOUT_UPDATE_PORTIN_DATE,
            payload: Ajax_post(
                {
                    url: API_URL_UPDATE_PORTIN_DATE,
                    data: {
                        portinDate,
                        memberId,
                        portingDateSelectionType,
                    },
                },
                dispatch,
            ),
            meta: {
                flashOnError: {
                    defaultErrorMessage: TEXT.ERROR.UPDATE_PORTIN,
                },
            },
        });
    };

export const getHolidayData =
    (): ThunkAction<Promise<{ holidayData: HolidayData }>, RootState, unknown, AnyAction> => async (dispatch) => {
        const response: Promise<{ holidayData: HolidayData }> = Ajax_get(
            {
                url: API_URL_GET_HOLIDAY_DATA,
            },
            dispatch,
        );

        return response;
    };
